// ===============================
// 📁 AXOIX Currency Context
// Multi-currency support infrastructure
// ===============================

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Supported currencies with exchange rates (base: INR)
export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  rate: number; // Rate relative to INR (1 INR = X currency)
  locale: string;
}

export const currencies: CurrencyConfig[] = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 1, locale: 'en-IN' },
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 0.012, locale: 'en-US' },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.011, locale: 'de-DE' },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.0095, locale: 'en-GB' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', rate: 0.044, locale: 'ar-AE' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', rate: 0.016, locale: 'en-SG' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 0.018, locale: 'en-AU' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 1.79, locale: 'ja-JP' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', rate: 0.086, locale: 'zh-CN' },
];

// Pricing tiers in INR (base currency)
// NOTE: This must match PRICING_TIERS in api/pricing.tsx
// Prices shown are AFTER 40% introductory discount
export const pricingINR = {
  visitor: {
    monthly: 0, yearly: 0, fiveYear: null, tenYear: null,
    originalMonthly: 0, originalYearly: 0, originalFiveYear: null, originalTenYear: null
  },
  crewMember: {
    monthly: 2999, yearly: 24999, fiveYear: null, tenYear: null,
    originalMonthly: 4999, originalYearly: 41999, originalFiveYear: null, originalTenYear: null
  },
  officer: {
    monthly: null, yearly: 49999, fiveYear: null, tenYear: null,
    originalMonthly: null, originalYearly: 83999, originalFiveYear: null, originalTenYear: null
  },
  commander: {
    monthly: null, yearly: null, fiveYear: 175999, tenYear: 300000,
    originalMonthly: null, originalYearly: null, originalFiveYear: 299999, originalTenYear: 499999
  },
  enterprise: { custom: true },
};

interface CurrencyContextType {
  currency: CurrencyConfig;
  setCurrency: (code: string) => void;
  formatPrice: (priceINR: number, showSymbol?: boolean) => string;
  convertPrice: (priceINR: number) => number;
  getPricing: () => typeof pricingINR;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<CurrencyConfig>(currencies[0]); // Default to INR

  // Load saved currency preference
  useEffect(() => {
    const savedCurrency = localStorage.getItem('axoix_currency');
    if (savedCurrency) {
      const found = currencies.find(c => c.code === savedCurrency);
      if (found) setCurrencyState(found);
    } else {
      // Auto-detect based on timezone (more reliable than navigator.language)
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const tzCurrencyMap: Record<string, string> = {
        'Asia/Kolkata': 'INR',
        'Asia/Calcutta': 'INR',
        'Asia/Mumbai': 'INR',
        'America/New_York': 'USD',
        'America/Chicago': 'USD',
        'America/Denver': 'USD',
        'America/Los_Angeles': 'USD',
        'Europe/London': 'GBP',
        'Europe/Berlin': 'EUR',
        'Europe/Paris': 'EUR',
        'Europe/Rome': 'EUR',
        'Asia/Dubai': 'AED',
        'Asia/Singapore': 'SGD',
        'Australia/Sydney': 'AUD',
        'Asia/Tokyo': 'JPY',
        'Asia/Shanghai': 'CNY',
      };
      const detectedCode = tzCurrencyMap[tz];
      const detected = detectedCode ? currencies.find(c => c.code === detectedCode) : null;
      // Default to INR if timezone not recognized (India-first product)
      if (detected) {
        setCurrencyState(detected);
      }
      // If no match, stays on default INR (currencies[0])
    }
  }, []);

  const setCurrency = (code: string) => {
    const found = currencies.find(c => c.code === code);
    if (found) {
      setCurrencyState(found);
      localStorage.setItem('axoix_currency', code);
    }
  };

  const convertPrice = (priceINR: number): number => {
    return Math.round(priceINR * currency.rate);
  };

  const formatPrice = (priceINR: number, showSymbol = true): string => {
    const converted = convertPrice(priceINR);

    try {
      const formatted = new Intl.NumberFormat(currency.locale, {
        style: showSymbol ? 'currency' : 'decimal',
        currency: currency.code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(converted);

      return formatted;
    } catch {
      return showSymbol ? `${currency.symbol}${converted.toLocaleString()}` : converted.toLocaleString();
    }
  };

  const getPricing = () => {
    // Return pricing converted to current currency
    // Matches structure of PRICING_TIERS in api/pricing.tsx
    return {
      visitor: {
        monthly: convertPrice(pricingINR.visitor.monthly),
        yearly: convertPrice(pricingINR.visitor.yearly),
        fiveYear: null,
        tenYear: null,
        originalMonthly: convertPrice(pricingINR.visitor.originalMonthly),
        originalYearly: convertPrice(pricingINR.visitor.originalYearly),
        originalFiveYear: null,
        originalTenYear: null,
      },
      crewMember: {
        monthly: convertPrice(pricingINR.crewMember.monthly),
        yearly: convertPrice(pricingINR.crewMember.yearly),
        fiveYear: null,
        tenYear: null,
        originalMonthly: convertPrice(pricingINR.crewMember.originalMonthly),
        originalYearly: convertPrice(pricingINR.crewMember.originalYearly),
        originalFiveYear: null,
        originalTenYear: null,
      },
      officer: {
        monthly: null,
        yearly: convertPrice(pricingINR.officer.yearly!),
        fiveYear: null,
        tenYear: null,
        originalMonthly: null,
        originalYearly: convertPrice(pricingINR.officer.originalYearly!),
        originalFiveYear: null,
        originalTenYear: null,
      },
      commander: {
        monthly: null,
        yearly: null,
        fiveYear: convertPrice(pricingINR.commander.fiveYear!),
        tenYear: convertPrice(pricingINR.commander.tenYear!),
        originalMonthly: null,
        originalYearly: null,
        originalFiveYear: convertPrice(pricingINR.commander.originalFiveYear!),
        originalTenYear: convertPrice(pricingINR.commander.originalTenYear!),
      },
      enterprise: {
        custom: true,
      },
    };
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, convertPrice, getPricing }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
